import {
  DEVICES_PAGINATED_FETCH_STARTED,
  DEVICES_PAGINATED_FETCH_SUCCEEDED,
  DEVICES_PAGINATED_FETCH_FAILED,
  DEVICES_PAGINATED_CHANGE_PAGE,
  DEVICES_PAGINATED_CHANGE_ITEMS_PER_PAGE,
  DEVICES_PAGINATED_CHANGE_ORDER,
  DEVICES_PAGINATED_CHANGE_ORDER_BY,
  DEVICES_PAGINATED_CHANGE_ORDER_AND_ORDER_BY,
  DEVICES_PAGINATED_CLEAR
} from '#action/types';
import { webPortalApi } from '#config/web-portal-api';
import { snackBarError } from '#action/fetch';
import { getRequestOptions, getURLParameter } from '#helper';

let abortController = new AbortController();

export const fetchDevices = () => async (dispatch, getState) => {
  try {
    dispatch({
      type: DEVICES_PAGINATED_FETCH_STARTED
    });

    const {
      page,
      itemsPerPage,
      order,
      orderBy
    } = getState().devicesPaginated;
    abortController.abort();
    const limit = itemsPerPage;
    const p = getURLParameter('page') || page;
    const offset = (Number(p) - 1) * itemsPerPage;
    const { device } = getState().listFilter;
    const searchValue = device?.search?.value;
    const deviceTypeValue = device?.deviceType?.value;
    const projectValue = device?.project?.value;
    const assignedStatus = device?.occupied?.value?.[0]?.data?.value;
    const searchParams = new URLSearchParams({ order, orderBy, limit, offset, pagination: 1 });

    if (deviceTypeValue) {
      const types = deviceTypeValue.map(v => v.data.uid).join(',');

      if (types) {
        searchParams.set('types', types);
      }
    }

    if (projectValue) {
      const ids = projectValue.map(v => v.data.id).join(',');

      if (ids) {
        searchParams.set('projects', ids);
      }
    }

    if (assignedStatus === '1') {
      searchParams.set('occupied', 'only');
      searchParams.set('occupierDetails', 1);
    } else if (assignedStatus === '2') {
      searchParams.set('occupied', 'without');
    } else {
      searchParams.delete('occupied');
      searchParams.set('occupierDetails', 1);
    }

    if (searchValue) {
      searchParams.set('filter', searchValue);
      searchParams.set('filterBy', 'title');
      searchParams.set('orderBy', 'title');
    }

    const url = webPortalApi.buildApiUrl(`device/distinct/details?${searchParams.toString()}`);

    abortController = new AbortController();
    const { signal } = abortController;
    // Debounce to do not send multiple requests in short time
    await new Promise(res => {
      setTimeout(() => {
        res();
      }, 200);
    });
    const res = await fetch(url, { ...getRequestOptions(), signal });
    const contentType = res.headers.get('content-type');

    if (!contentType || !contentType.includes('application/json')) {
      throw new TypeError('Response is not json');
    }
    if (!res.ok) {
      dispatch(
        snackBarError({
          message: 'Error in request'
        })
      );
    }

    const { devices = [], last } = await res.json();

    dispatch({
      type: DEVICES_PAGINATED_FETCH_SUCCEEDED,
      payload: devices,
      totalCount: last
    });
  } catch (e) {
    if (e.name === 'AbortError') {
      return;
    }
    dispatch({
      type: DEVICES_PAGINATED_FETCH_FAILED,
      error: e
    });
  }
};

export const changePage = (newPage, { refetch = true } = {}) => dispatch => {
  dispatch({
    type: DEVICES_PAGINATED_CHANGE_PAGE,
    payload: newPage
  });

  if (refetch) {
    dispatch(fetchDevices());
  }
};

export const changeItemsPerPage = newItemsPerPage => dispatch => {
  dispatch({
    type: DEVICES_PAGINATED_CHANGE_ITEMS_PER_PAGE,
    payload: newItemsPerPage
  });
  dispatch(fetchDevices());
};

export const changeOrder = (newOrder, { refetch = true } = {}) => dispatch => {
  dispatch({
    type: DEVICES_PAGINATED_CHANGE_ORDER,
    payload: newOrder
  });

  if (refetch) {
    dispatch(fetchDevices());
  }
};

export const changeOrderBy = (newOrderBy, { refetch = true } = {}) => dispatch => {
  dispatch({
    type: DEVICES_PAGINATED_CHANGE_ORDER_BY,
    payload: newOrderBy
  });

  if (refetch) {
    dispatch(fetchDevices());
  }
};

export const changeOrderAndOrderBy = (
  newOrder,
  newOrderBy,
  { refetch = true } = {}
) => dispatch => {
  dispatch({
    type: DEVICES_PAGINATED_CHANGE_ORDER_AND_ORDER_BY,
    payload: { order: newOrder, orderBy: newOrderBy }
  });

  if (refetch) {
    dispatch(fetchDevices());
  }
};

export const clear = () => dispatch => {
  abortController.abort();
  dispatch({
    type: DEVICES_PAGINATED_CLEAR
  });
};
